import {pick} from 'lodash-es';
import {useEffect, useReducer, useState} from 'react';

import {processorService} from '@/services/processorService';
import type {AfriexProcessor} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';
import {ProcessorValidator} from '@/validators/ProcessorValidator';

import {ProcessorActionType} from './types';
import updateProcessorReducer from './updateProcessorReducer';

const useProcessorUpdate = (
  onClose: VoidFunction,
  initialData: Partial<AfriexProcessor> = {
    meta: {
      flags: {
        isActive: true,
        isDeactivated: false,
        isDown: false,
        isFunded: true,
      },
    },
  },
) => {
  const [processor, dispatch] = useReducer(
    updateProcessorReducer,
    initialData as AfriexProcessor,
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch({
      type: ProcessorActionType.UPDATE_PROCESSOR,
      payload: initialData,
    });
  }, []);

  const handleSubmit = async (rawProcessor: AfriexProcessor) => {
    setIsLoading(true);
    try {
      const validProcessor = new ProcessorValidator(rawProcessor).value();

      // Processor records are created by the deploy, so the form only ever
      // updates an existing one.
      if (!rawProcessor.id) {
        throw new Error('Processor has no id');
      }
      // The server rejects `code`: it is what ties the record to its processor.
      const fieldsToInclude: (keyof AfriexProcessor)[] = ['name', 'meta'];
      await processorService.updateProcessorById(
        rawProcessor.id,
        pick(validProcessor, fieldsToInclude),
      );
      showSuccessMessage('Processor updated successfully');

      onClose();

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (e: unknown) {
      showErrorMessage(
        `Error saving processor: ${getErrorMessage(e, String(e))}`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  return {
    dispatch,
    handleSubmit,
    isLoading,
    processor,
  };
};

export default useProcessorUpdate;
